import { BsModalRef } from 'ngx-bootstrap/modal'
import { Component, Input, OnInit } from '@angular/core'
import { SaleItem } from '../sale-item'
import { AppState } from 'src/app/core/core.state'
import { Store } from '@ngrx/store'
import { saleItemActions } from '../sale-item.actions'
import { Actions, ofType } from '@ngrx/effects'
import { combineLatest, Observable, of } from 'rxjs'
import { take, timeout, catchError, filter, map } from 'rxjs/operators'
import { NotificationService } from 'src/app/core/notification.service'
import { saleActions } from '../../sale.actions'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import moment from 'moment-mini'
import { saleSelectors } from '../../sale.selectors'
import { Sale } from '../../sale'
import { paymentSelectors } from '../../payment/payment.selectors'
import { SaleService } from '../../sale.service'
import { activityActions } from 'src/app/features/activity/activity.actions'
import { Activity, ActivityTypes } from 'src/app/features/activity/activity'
import { activitySelectors } from 'src/app/features/activity/activity.selectors'
import { CollectStatusType, PayMethodType } from '../../payment/payment'
import { paymentActions } from '../../payment/payment.actions'

@Component({
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SaleItemShowModalComponent implements OnInit {
  @Input() title: string
  @Input() saleItem: Partial<SaleItem>
  @Input() saleItemsOnBulkActions: Partial<SaleItem[]>
  private bulkEditFieldsChanges
  private sale$: Observable<Sale>
  private saleEvents: Activity[]
  public isSalePaid$: Observable<boolean>

  constructor(
    private bsModalRef: BsModalRef,
    private store: Store<AppState>,
    private actions$: Actions,
    private notificationService: NotificationService,
    private saleService: SaleService
  ) {}

  ngOnInit() {
    // get saleId from either saleItem or first item in bulk actions
    const currentSaleItem = this.saleItem ?? this.saleItemsOnBulkActions?.[0]
    const saleId = currentSaleItem?.saleId

    this.sale$ = this.store.select(saleSelectors.selectById, saleId)
    this.store
      .select(activitySelectors.selectByFilter, {
        field: 'saleId',
        value: saleId
      })
      .pipe(
        filter((activities) => Boolean(activities.length)),
        take(1)
      )
      .subscribe((activities) => {
        this.saleEvents = activities
      })
    this.store.dispatch(
      activityActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: { saleId: saleId?.toString() || '' }
        })
      })
    )
    this.store.dispatch(paymentActions.resetState())
    this.store.dispatch(
      saleActions.fetchOneById({
        id: saleId,
        params: RequestQueryBuilder.create({
          join: [{ field: 'payments' }]
        })
      })
    )

    this.isSalePaid$ = combineLatest([
      this.sale$,
      this.store.select(paymentSelectors.selectByFilter, {
        field: 'saleId',
        value: saleId
      })
    ]).pipe(
      filter(([sale, _]) => Boolean(sale)),
      map(([sale, payments]) => {
        const hasUndecidedCollectablePayments = payments.some(
          (payment) =>
            payment.collectStatus ==
              CollectStatusType.COLLECT_STATUS_COLLECTABLE &&
            payment.payMethod === PayMethodType.PAY_METHOD_UNDECIDED
        )
        return !hasUndecidedCollectablePayments
      })
    )
  }

  public cancel() {
    this.bsModalRef.hide()
  }

  public onSubmit(saleItem: Partial<SaleItem>) {
    const formData: any = { ...saleItem }

    delete formData?.bookingItemsId
    // delete formData?.tags
    delete formData?.startDate
    delete formData?.startTime
    delete formData?.endTime
    delete formData?.selectedBookingItemsTime

    if (this.saleItem.saleId) {
      if (formData.params) {
        this.store.dispatch(
          saleItemActions.updateOneBySaleId({
            saleId: this.saleItem.saleId,
            saleLine: this.saleItem.saleLine,
            params: {
              ...formData,
              customerCategory: formData.customerCategory
                ? +formData.customerCategory
                : null
            }
          })
        )
      } else {
        this.store.dispatch(
          saleItemActions.updateOneBySaleId({
            saleId: this.saleItem.saleId,
            saleLine: this.saleItem.saleLine,
            params: {
              participantsAdjustment: formData.participantsAdjustment,
              ...formData,
              customerCategory: formData.customerCategory
                ? +formData.customerCategory
                : null
            }
          })
        )
      }

      this.actions$
        .pipe(
          ofType(saleItemActions.upsertMany),
          take(1),
          timeout(50000),
          catchError((error) => of(null))
        )
        .subscribe((action) => {
          if (action) {
            this.store.dispatch(
              saleItemActions.fetchOneById({
                id: this.saleItem.id,
                params: RequestQueryBuilder.create({
                  join: [
                    { field: 'product' },
                    { field: 'prodcuct.linkedSubscription' },
                    { field: 'subscription' },
                    { field: 'course' },
                    { field: 'tags' },
                    { field: 'rental' }
                  ]
                })
              })
            )
            this.store.dispatch(
              saleActions.fetchOneById({
                id: this.saleItem.saleId,
                params: RequestQueryBuilder.create({
                  join: [
                    { field: 'creditSales' },
                    { field: 'saleItems' },
                    { field: 'saleItems.booking' },
                    { field: 'saleItems.booking.product' },
                    { field: 'saleItems.booking.linkedProduct' },
                    { field: 'saleItems.product' },
                    { field: 'saleItems.product.translations' },
                    { field: 'saleItems.product.parentBookableProducts' },
                    { field: 'saleItems.participations' },
                    { field: 'payments' }
                  ]
                })
              })
            )
            this.cancel()
          }
          this.store.dispatch(
            activityActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: { saleId: this.saleItem?.saleId?.toString() || '' }
              })
            })
          )
        })

      this.actions$
        .pipe(
          ofType(saleActions.upsertMany),
          take(1),
          timeout(50000),
          catchError((error) => of(null))
        )
        .subscribe((action) => {
          if (action) {
            this.notificationService.emit('alerts.success', 'success')
            this.cancel()
          }
        })
    } else {
      this.store.dispatch(
        saleItemActions.updateOnePartial({
          id: this.saleItem?.id,
          entity: {
            ...this.saleItem,
            ...formData
          }
        })
      )
      this.cancel()
    }
  }
  public isFatherVisible(visibleFather) {
    if (!visibleFather) this.cancel()
  }
  public onBulkEditFieldsChanges(bulkEditFieldsChanges) {
    this.bulkEditFieldsChanges = bulkEditFieldsChanges
  }
  public onSubmitBulk(saleItem: any) {
    this.saleItemsOnBulkActions.forEach((saleIt: any, idx) => {
      delete saleIt?.checked

      if (saleItem.quantity < saleIt.quantity) {
        let startDate =
          this.bulkEditFieldsChanges['startDate'] &&
          this.bulkEditFieldsChanges['startTime']
            ? moment(saleItem.startDate)
                .set('hours', moment(saleItem.startTime).get('hours'))
                .set('minutes', moment(saleItem.startTime).get('minutes'))
                .set('seconds', 0)
                .set('milliseconds', 0)
                .toISOString()
            : this.bulkEditFieldsChanges['startDate'] &&
                !this.bulkEditFieldsChanges['startTime']
              ? moment(saleItem.startDate)
                  .set(
                    'hours',
                    moment(saleIt.params?.startDate ?? 0).get('hours')
                  )
                  .set(
                    'minutes',
                    moment(saleIt.params?.startDate ?? 0).get('minutes')
                  )
                  .set('seconds', 0)
                  .set('milliseconds', 0)
                  .toISOString()
              : !this.bulkEditFieldsChanges['startDate'] &&
                  this.bulkEditFieldsChanges['startTime'] &&
                  moment(saleIt.params?.startDate).isValid()
                ? moment(saleIt.params?.startDate)
                    .set('hours', moment(saleItem.startTime).get('hours'))
                    .set('minutes', moment(saleItem.startTime).get('minutes'))
                    .set('seconds', 0)
                    .set('milliseconds', 0)
                    .toISOString()
                : saleIt.params?.startDate
        this.store.dispatch(
          saleItemActions.updateOneBySaleId({
            saleId: saleIt.saleId,
            saleLine: saleIt.saleLine,
            params: {
              ...saleIt,
              customerCategory: this.bulkEditFieldsChanges['customerCategory']
                ? Number(saleItem.customerCategory)
                : saleIt.customerCategory,
              quantity: this.bulkEditFieldsChanges['quantity']
                ? saleItem.quantity
                : saleIt.quantity,
              tags: this.bulkEditFieldsChanges['tags']
                ? [...saleItem['tags']]
                : saleIt.tags,
              params: {
                ...saleIt.params,
                bookableProductId: this.bulkEditFieldsChanges[
                  'bookableProductId'
                ]
                  ? saleItem.bookableProductId
                  : saleIt.params?.bookableProductId,
                endDate:
                  this.bulkEditFieldsChanges['startDate'] &&
                  this.bulkEditFieldsChanges['endTime']
                    ? moment(saleItem.startDate)
                        .set('hours', moment(saleItem.endTime).get('hours'))
                        .set('minutes', moment(saleItem.endTime).get('minutes'))
                        .set('seconds', 0)
                        .set('milliseconds', 0)
                        .toISOString()
                    : this.bulkEditFieldsChanges['startDate'] &&
                        !this.bulkEditFieldsChanges['endTime'] &&
                        moment(moment(saleItem?.startDate)).isValid()
                      ? moment(saleItem.startDate)
                          .set(
                            'hours',
                            moment(saleIt.params?.endDate ?? 0).get('hours')
                          )
                          .set(
                            'minutes',
                            moment(saleIt.params?.endDate ?? 0).get('minutes')
                          )
                      : !this.bulkEditFieldsChanges['startDate'] &&
                          this.bulkEditFieldsChanges['endTime'] &&
                          moment(moment(saleIt.params?.startDate)).isValid()
                        ? moment(saleIt.params?.startDate)
                            .set('hours', moment(saleItem.endTime).get('hours'))
                            .set(
                              'minutes',
                              moment(saleItem.endTime).get('minutes')
                            )
                            .set('seconds', 0)
                            .set('milliseconds', 0)
                            .toISOString()
                        : saleIt.params?.endDate,
                startDate
              },
              cancelationPolicyUuid: saleItem.cancelationPolicyUuid
            }
          })
        )
      } else {
        let startDate =
          this.bulkEditFieldsChanges['startDate'] &&
          this.bulkEditFieldsChanges['startTime']
            ? moment(saleItem.startDate)
                .set('hours', moment(saleItem.startTime).get('hours'))
                .set('minutes', moment(saleItem.startTime).get('minutes'))
                .set('seconds', 0)
                .set('milliseconds', 0)
                .toISOString()
            : this.bulkEditFieldsChanges['startDate'] &&
                !this.bulkEditFieldsChanges['startTime']
              ? moment(saleItem.startDate)
                  .set(
                    'hours',
                    moment(saleIt.params?.startDate ?? 0).get('hours')
                  )
                  .set(
                    'minutes',
                    moment(saleIt.params?.startDate ?? 0).get('minutes')
                  )
                  .set('seconds', 0)
                  .set('milliseconds', 0)
                  .toISOString()
              : !this.bulkEditFieldsChanges['startDate'] &&
                  this.bulkEditFieldsChanges['startTime'] &&
                  moment(saleIt.params?.startDate).isValid()
                ? moment(saleIt.params?.startDate)
                    .set('hours', moment(saleItem.startTime).get('hours'))
                    .set('minutes', moment(saleItem.startTime).get('minutes'))
                    .set('seconds', 0)
                    .set('milliseconds', 0)
                    .toISOString()
                : saleIt.params?.startDate
        this.store.dispatch(
          saleItemActions.updateOneBySaleId({
            saleId: saleIt.saleId,
            saleLine: saleIt.saleLine,
            params: {
              ...saleIt,
              customerCategory: this.bulkEditFieldsChanges['customerCategory']
                ? Number(saleItem.customerCategory)
                : saleIt.customerCategory,
              quantity: this.bulkEditFieldsChanges['quantity']
                ? saleItem.quantity
                : saleIt.quantity,
              tags: this.bulkEditFieldsChanges['tags']
                ? [...saleItem['tags']]
                : saleIt.tags,
              params: {
                ...saleIt.params,
                bookableProductId: this.bulkEditFieldsChanges[
                  'bookableProductId'
                ]
                  ? saleItem.bookableProductId
                  : saleIt.params?.bookableProductId,
                endDate:
                  this.bulkEditFieldsChanges['startDate'] &&
                  this.bulkEditFieldsChanges['endTime']
                    ? moment(saleItem.startDate)
                        .set('hours', moment(saleItem.endTime).get('hours'))
                        .set('minutes', moment(saleItem.endTime).get('minutes'))
                        .set('seconds', 0)
                        .set('milliseconds', 0)
                        .toISOString()
                    : this.bulkEditFieldsChanges['startDate'] &&
                        !this.bulkEditFieldsChanges['endTime'] &&
                        moment(moment(saleItem?.startDate)).isValid()
                      ? moment(saleItem.startDate)
                          .set(
                            'hours',
                            moment(saleIt.params?.endDate ?? 0).get('hours')
                          )
                          .set(
                            'minutes',
                            moment(saleIt.params?.endDate ?? 0).get('minutes')
                          )
                      : !this.bulkEditFieldsChanges['startDate'] &&
                          this.bulkEditFieldsChanges['endTime'] &&
                          moment(moment(saleIt.params?.startDate)).isValid()
                        ? moment(saleIt.params?.startDate)
                            .set('hours', moment(saleItem.endTime).get('hours'))
                            .set(
                              'minutes',
                              moment(saleItem.endTime).get('minutes')
                            )
                            .set('seconds', 0)
                            .set('milliseconds', 0)
                            .toISOString()
                        : saleIt.params?.endDate,
                startDate
              }
            }
          })
        )
      }
    })
    let totalCallsToDo = 0
    this.actions$
      .pipe(
        ofType(saleItemActions.upsertMany, saleItemActions.upsertOne),
        take(this.saleItemsOnBulkActions?.length ?? 1),
        timeout(6000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (!action) {
          this.cancel()
        }
        totalCallsToDo = totalCallsToDo + 1
        if (totalCallsToDo == this.saleItemsOnBulkActions?.length) {
          this.cancel()
        }
      })
  }
  public onSubmitDelete(params: any) {
    const saleId = this.saleItem.saleId

    if (!saleId) {
      // This is a partial saleitem without sale, in sale create flow
      this.store.dispatch(
        saleItemActions.removeOnePartial({ id: this.saleItem.id })
      )
      return
    }

    const saleItemRefundEvents = this.saleEvents?.filter(
      (event) =>
        event.saleItemId === this.saleItem.id &&
        event.typeId === ActivityTypes.SALE_ITEM_REFUNDED
    )

    const remainingAmountToRefund =
      saleItemRefundEvents?.length > 0
        ? Math.min(...saleItemRefundEvents.map((event) => event.toValue))
        : this.saleItem.quantity

    this.store.dispatch(
      saleItemActions.updateOneBySaleId({
        saleId: this.saleItem.saleId,
        saleLine: this.saleItem.saleLine,
        params: {
          cancelationPolicyUuid: params.cancelationPolicyUuid,
          quantity: remainingAmountToRefund,
          deleteSaleItem: true
        }
      })
    )

    this.actions$
      .pipe(
        ofType(
          saleItemActions.upsertMany,
          saleItemActions.upsertOne,
          saleItemActions.removeOne
        ),
        take(1),
        timeout(50000),
        catchError((error) => of(null))
      )
      .subscribe((action) => {
        if (action) {
          this.store.dispatch(
            saleActions.fetchOneById({
              id: saleId,
              params: RequestQueryBuilder.create({
                join: [
                  { field: 'creditSales' },
                  { field: 'saleItems' },
                  { field: 'saleItems.booking' },
                  { field: 'saleItems.booking.product' },
                  { field: 'saleItems.booking.linkedProduct' },
                  { field: 'saleItems.product' },
                  { field: 'saleItems.product.translations' },
                  { field: 'saleItems.product.parentBookableProducts' },
                  { field: 'saleItems.participations' },
                  { field: 'payments' }
                ]
              })
            })
          )
          this.notificationService.emit('alerts.success', 'success')
          this.cancel()
        }
      })
  }
}
